import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../libs/axios';
import Breadcrumb from '../../utils/breadcrumbs';
import { ListLoader } from '../../utils/loader';

interface AppSetting { key: string; value: string | null; }

const LINK_FIELDS: { key: string; label: string; hint: string }[] = [
  { key: 'help_faqs_url', label: 'Help / FAQs', hint: 'Opened from the app Help & FAQs link' },
  { key: 'privacy_policy_url', label: 'Privacy Policy', hint: 'Opened from Settings > Privacy Policy' },
  { key: 'terms_of_service_url', label: 'Terms of Service', hint: 'Opened from Settings > Terms of Service' },
  { key: 'about_us_url', label: 'About Us', hint: 'Opened from Settings > About Us' },
  { key: 'android_store_url', label: 'Android Store URL (Play Store)', hint: 'Used by Broadcast Push → "Use App Update template". Tapping the notification opens this on Android.' },
  { key: 'ios_store_url', label: 'iOS Store URL (App Store)', hint: 'Used by Broadcast Push → "Use App Update template". Tapping the notification opens this on iOS.' },
];

const AppLinks: React.FC = () => {
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // ADM-2: Save stays off until the live values have loaded once; otherwise a failed load
  // left an empty form whose Save wrote blanks over every live link.
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res: any = await api.get('/app-settings');
      const rows: AppSetting[] = res.data || [];
      const map: Record<string, string> = {};
      rows.forEach((r) => { map[r.key] = r.value || ''; });
      setValues(map);
      setLoaded(true);
    } catch (e) {
      toast.error('Failed to load app links. Saving is off until they load; reload the page.');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onChange = (key: string, v: string) => setValues((p) => ({ ...p, [key]: v }));

  const onSave = async () => {
    if (!loaded) { toast.error('The live links have not loaded, so nothing was saved. Reload the page and try again.'); return; }
    setSaving(true);
    try {
      const settings: Record<string, string> = {};
      LINK_FIELDS.forEach((f) => { settings[f.key] = values[f.key] || ''; });
      await api.put('/app-settings', { settings });
      toast.success('App links updated');
      load();
    } catch (e) {
      toast.error('Failed to save app links');
    } finally { setSaving(false); }
  };

  return (
    <div className="p-4 sm:p-6">
      <Breadcrumb />
      <div className="mt-4 mb-6">
        <h1 className="text-xl font-semibold text-gray-900">App Links</h1>
        <p className="text-sm text-gray-500">Links shown in the mobile app. Changing a link here updates it in the app.</p>
      </div>

      {loading ? <ListLoader /> : (
        <div className="bg-white border border-gray-200 rounded-xl p-5 sm:p-6 max-w-3xl">
          <div className="space-y-5">
            {LINK_FIELDS.map((f) => (
              <div key={f.key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{f.label}</label>
                <input
                  type="url"
                  value={values[f.key] || ''}
                  onChange={(e) => onChange(f.key, e.target.value)}
                  placeholder="https://..."
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="text-xs text-gray-400 mt-1">{f.hint}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 flex justify-end">
            <button onClick={onSave} disabled={saving || !loaded}
              className="bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-60">
              {saving ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppLinks;
